import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Search, SlidersHorizontal, Trash2, UserPlus } from "lucide-react";
import { toast } from "react-toastify";

import Layout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import TableV2 from "src/components/ui/TableV2";
import FilterSelect from "src/components/ui/FilterSelect";
import { Badge } from "src/components/ui/Badge";
import Modal from "src/components/ui/Modal";

import { formatDateTime } from "src/utils/date-time";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as signupRequestActions from "src/redux/slices/tenantSignupRequestSlice";
import { TenantSignupRequest } from "src/types/tenantSignupRequest.types";

const STATUS_OPTIONS = [
  { label: "Pending review", value: "pending_review" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const STATUS_BADGE: Record<string, { text: string; color: "green" | "red" | "yellow" }> = {
  pending_review: { text: "PENDING REVIEW", color: "yellow" },
  approved: { text: "APPROVED", color: "green" },
  rejected: { text: "REJECTED", color: "red" },
};

const SignupRequestsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { list, approve, reject, delete: deleteState } = useAppSelector(
    (state) => state.tenantSignupRequest
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<TenantSignupRequest | null>(null);
  const [confirmingApprove, setConfirmingApprove] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<TenantSignupRequest | null>(null);
  const limit = 10;
  const hasActiveFilters = Boolean(search || status);

  const getList = () =>
    dispatch(
      signupRequestActions.getSignupRequestsListRequest({
        page: currentPage,
        limit,
        ...(status ? { status } : {}),
        ...(search ? { search } : {}),
      })
    );

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    getList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, status, search]);

  useEffect(() => {
    if (approve.data) {
      toast.success("Request approved — workspace is live.");
      dispatch(signupRequestActions.resetApproveSignupRequest());
      setSelected(null);
      setConfirmingApprove(false);
      getList();
    } else if (approve.error) {
      toast.error(`Error: ${approve.error}`);
      dispatch(signupRequestActions.resetApproveSignupRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approve]);

  useEffect(() => {
    if (reject.data) {
      toast.success("Request rejected.");
      dispatch(signupRequestActions.resetRejectSignupRequest());
      setSelected(null);
      getList();
    } else if (reject.error) {
      toast.error(`Error: ${reject.error}`);
      dispatch(signupRequestActions.resetRejectSignupRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reject]);

  useEffect(() => {
    if (deleteState.data) {
      toast.success("Request deleted.");
      dispatch(signupRequestActions.resetDeleteSignupRequest());
      setDeleteTarget(null);
      getList();
    } else if (deleteState.error) {
      toast.error(`Error: ${deleteState.error}`);
      dispatch(signupRequestActions.resetDeleteSignupRequest());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteState]);

  const handleApprove = (id: string) =>
    dispatch(signupRequestActions.approveSignupRequestRequest({ id }));

  const handleReject = (id: string) =>
    dispatch(signupRequestActions.rejectSignupRequestRequest({ id }));

  const handleDelete = (id: string) =>
    dispatch(signupRequestActions.deleteSignupRequestRequest({ id }));

  return (
    <Layout
      type="admin"
      meta={{
        title: "Sign-up Requests | Invyntra",
        description: "Review and approve self-serve tenant sign-up requests.",
        keywords: "signup, requests, tenants, invyntra",
      }}
    >
      <div className="space-y-4">
        {/* Page header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
              <UserPlus size={26} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Sign-up Requests
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                New workspaces wait here until approved — approving
                provisions the tenant and starts the 15-day trial.
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <div className="flex w-full items-center justify-between">
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <SlidersHorizontal size={14} />
              Filters
              {hasActiveFilters && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              rounded="full"
              onClick={() => setFiltersOpen((o) => !o)}
            >
              {filtersOpen ? "Hide filters" : "Show filters"}
              {filtersOpen ? (
                <ChevronUp size={16} className="ml-1.5" />
              ) : (
                <ChevronDown size={16} className="ml-1.5" />
              )}
            </Button>
          </div>

          {filtersOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gray-100 pt-4 dark:border-gray-700 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search
                </label>
                <Input
                  placeholder="Search requests..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  startAdornment={<Search size={14} />}
                />
              </div>

              <FilterSelect
                variant="boxed"
                label="Status"
                value={status}
                onChange={(v) => {
                  setStatus(v);
                  setCurrentPage(1);
                }}
                allLabel="All statuses"
                options={STATUS_OPTIONS}
              />
            </div>
          )}
        </Card>

        <Card>
          <TableV2
            columns={[
              {
                key: "requestId",
                header: "Request ID",
                accessor: ({ row }) => (
                  <button
                    className="font-mono text-indigo-600 hover:underline"
                    onClick={() => setSelected(row)}
                  >
                    #{row.requestId}
                  </button>
                ),
              },
              { key: "organizationName", header: "Organization" },
              { key: "ownerName", header: "Owner" },
              { key: "email", header: "Email" },
              { key: "mobile", header: "Mobile" },
              {
                key: "status",
                header: "Status",
                accessor: ({ row }) => {
                  const badge = STATUS_BADGE[row.status] || STATUS_BADGE.pending_review;
                  return <Badge text={badge.text} color={badge.color} />;
                },
              },
              {
                key: "createdAt",
                header: "Submitted",
                accessor: ({ row }) => (
                  <span>{formatDateTime(row.createdAt, "DD MMM YYYY")}</span>
                ),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) => (
                  <div className="flex items-center gap-2">
                    {row.status === "pending_review" ? (
                      <Button size="sm" onClick={() => setSelected(row)}>
                        Review
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {row.status === "approved" ? "Approved" : "Rejected"}
                      </span>
                    )}
                    {row.status !== "approved" && (
                      <button
                        title="Delete request"
                        onClick={() => setDeleteTarget(row)}
                        className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      >
                        <Trash2 size={15} />
                      </button>
                    )}
                  </div>
                ),
              },
            ]}
            data={list?.data?.data}
            pagination={list?.data?.pagination}
            onPageChange={(page) => setCurrentPage(page)}
            loading={list.loading}
          />
        </Card>

        {selected && (
          <Modal
            open={!!selected}
            onClose={() => {
              setSelected(null);
              setConfirmingApprove(false);
            }}
            title={`Sign-up Request #${selected.requestId}`}
            size="md"
            footer={
              selected.status === "pending_review" ? (
                confirmingApprove ? (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => setConfirmingApprove(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      isLoading={approve.loading}
                      onClick={() => handleApprove(selected._id)}
                    >
                      Confirm &amp; approve
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      color="danger"
                      isLoading={reject.loading}
                      onClick={() => handleReject(selected._id)}
                    >
                      Reject
                    </Button>
                    <Button onClick={() => setConfirmingApprove(true)}>
                      Approve
                    </Button>
                  </>
                )
              ) : undefined
            }
          >
            <div className="space-y-3 text-sm">
              <Row label="Organization" value={selected.organizationName} />
              <Row label="Identifier" value={selected.organizationIdentifier} />
              <Row label="Owner" value={selected.ownerName} />
              <Row label="Email" value={selected.email} />
              <Row label="Mobile" value={selected.mobile} />
              <Row
                label="Submitted"
                value={formatDateTime(selected.createdAt, "DD MMM YYYY, hh:mm A")}
              />
              {selected.reviewedAt && (
                <Row
                  label="Reviewed"
                  value={formatDateTime(selected.reviewedAt, "DD MMM YYYY, hh:mm A")}
                />
              )}
              {confirmingApprove && (
                <div className="rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  This creates a live Tenant, a default Admin role, a General
                  department, and the owner's login — then starts their
                  15-day trial. This can't be undone from here.
                </div>
              )}
            </div>
          </Modal>
        )}

        {deleteTarget && (
          <Modal
            open={!!deleteTarget}
            onClose={() => setDeleteTarget(null)}
            title="Delete sign-up request?"
            size="sm"
            footer={
              <>
                <Button variant="outline" onClick={() => setDeleteTarget(null)}>
                  Cancel
                </Button>
                <Button
                  color="danger"
                  isLoading={deleteState.loading}
                  onClick={() => handleDelete(deleteTarget._id)}
                >
                  Delete
                </Button>
              </>
            }
          >
            <p className="text-sm text-gray-600 dark:text-gray-300">
              This permanently removes the request from{" "}
              <b>{deleteTarget.organizationName}</b> (#{deleteTarget.requestId}).
              This can't be undone.
            </p>
          </Modal>
        )}
      </div>
    </Layout>
  );
};

const Row: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-gray-100 pb-2 dark:border-gray-700">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="font-medium text-gray-900 dark:text-gray-100">{value}</span>
  </div>
);

export default SignupRequestsPage;
